import { Avatar, Button, Chip } from "@heroui/react";
import { dateToLocalTime, formatInTz } from "@/utils/format-date-with-locale";
import { useBreakpoint } from "@/hooks/use-media-query";
import { useTranslations } from "../../../../../i18n";
import { EventStatus } from "@/types/EventStatus";
import { workerUrl } from "@/lib/b2";
import { useUserBookings } from "@/hooks/use-user-bookings";
import { useUserWaitlistBookings } from "@/hooks/use-user-waitlist-bookings";
import { IconCircleCheck, IconClock } from "@tabler/icons-react";

const timezone = "Europe/Zurich";
const getEventStart = (event: any) => event.startAt ?? event.startTime;

export function AgendaEvent({
  event,
  onClick,
  onWaitlistJoin,
}: {
  event: any;
  onClick: () => void;
  onWaitlistJoin?: (event: any) => void;
}) {
  const { hasBookedEvent } = useUserBookings();
  const { hasWaitlistedEvent } = useUserWaitlistBookings();

  const { isMobile } = useBreakpoint();
  const t = useTranslations("calendar");
  const startValue = getEventStart(event);
  if (!startValue) return null;
  const start = dateToLocalTime(new Date(startValue), timezone);
  const now = dateToLocalTime(new Date(), timezone);
  const isPastEvent = start.getTime() < now.getTime();

  const disabled =
    event.isCancelled ||
    event.status === EventStatus.CANCELED ||
    event.status === EventStatus.COMPLETED ||
    event.status === EventStatus.HOLIDAY;

  const teacherName = event.teacherName;
  const teacherAvatar = resolveTeacherAvatar(event.teacherAvatar);
  const canBook = !disabled;

  const isBooked = hasBookedEvent(event.id);
  const isWaitlisted = hasWaitlistedEvent(event.id);
  const isFull = Boolean(event.isFull);

  const handleWaitlistClick = () => {
    if (onWaitlistJoin) {
      onWaitlistJoin(event);
    } else {
      onClick();
    }
  };

  if (isMobile) {
    return (
      <div
        onClick={() => {
          if (disabled) return;
          if (isFull && !isBooked && !isWaitlisted) {
            handleWaitlistClick();
          } else {
            onClick();
          }
        }}
        className={`
            cursor-pointer rounded-2xl bg-muted/40 p-4 transition
            ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-muted/60"}
            `}
      >
        {/* Time */}
        <div className="text-lg font-medium text-gray-900 mb-1">
          {formatInTz(start, "HH:mm", timezone)}
        </div>

        {/* Class name */}
        <div className="text-2xl font-semibold text-primary-800 mb-4">
          <div className="flex items-center gap-2">
            {event.title ?? event.class?.name ?? t("classFallback")}
            {event.type === "WORKSHOP" && (
              <Chip size="sm" color="secondary" variant="flat">
                {t("workshop")}
              </Chip>
            )}
            {event.type === "RETREAT" && (
              <Chip size="sm" color="warning" variant="flat">
                {t("retreat")}
              </Chip>
            )}
          </div>
        </div>

        {/* Teacher */}
        <div className="flex items-center gap-3">
          <Avatar src={teacherAvatar} name={teacherName} size="md" />
          <div className="text-lg text-gray-800">{teacherName}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-4 hover:bg-gray-50 p-3 rounded-md transition cursor-pointer"
      onClick={() => {
        if (!canBook) return;
        if (isFull && !isBooked && !isWaitlisted) {
          handleWaitlistClick();
        } else {
          onClick();
        }
      }}
    >
      {/* Time */}
      <div className="w-[70px] text-gray-700 tabular-nums">
        {formatInTz(start, "HH:mm", timezone)}
      </div>

      {/* Teacher */}
      <div className="flex items-center gap-3 min-w-[140px]">
        <Avatar
          size="lg"
          src={teacherAvatar}
          name={teacherName}
          className={disabled ? "opacity-50" : ""}
        />
        <div className={`text-gray-800 ${disabled ? "opacity-50" : ""}`}>
          {teacherName}
        </div>
      </div>

      {/* Class name */}
      <div
        className={`flex-1 flex items-center gap-2 text-lg font-semibold ${disabled ? "text-gray-400" : "text-primary-800"}`}
      >
        {event.title ?? event.class?.name ?? ""}
        {event.type === "WORKSHOP" && (
          <Chip size="sm" color="secondary" variant="flat">
            {t("workshop")}
          </Chip>
        )}
        {event.type === "RETREAT" && (
          <Chip size="sm" color="warning" variant="flat">
            {t("retreat")}
          </Chip>
        )}
      </div>

      {/* CTA */}
      {isBooked ? (
        <div className="flex items-center text-left justify-start gap-1.5 text-sm text-success-500 font-medium">
          <IconCircleCheck size={16} />
          <span>{t("yourein")}</span>
        </div>
      ) : isWaitlisted ? (
        <div className="flex items-center text-left justify-start gap-1.5 text-sm text-warning-500 font-medium min-w-[120px]">
          <IconClock size={16} />
          <span>{t("onWaitlist")}</span>
        </div>
      ) : !isPastEvent ? (
        <Button
          radius="full"
          className="min-w-[120px]"
          color={canBook ? (isFull ? "warning" : "primary") : "default"}
          variant={isFull ? "bordered" : "solid"}
          isDisabled={!canBook}
          onPress={(e) => {
            (e as any).stopPropagation?.();
            if (canBook) {
              if (isFull) {
                handleWaitlistClick();
              } else {
                onClick();
              }
            }
          }}
        >
          {isFull ? t("joinWaitlist") : t("book")}
        </Button>
      ) : null}
    </div>
  );
}

function resolveTeacherAvatar(avatar?: string | null) {
  if (!avatar) return undefined;
  if (avatar.startsWith("http://") || avatar.startsWith("https://")) {
    return avatar;
  }
  return workerUrl(avatar);
}
